// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminHome.css";
import "./AdminAppStoreSettings.css";
import AdminTopNav from "./AdminTopNav";
import { useNavPinned } from "./useNavPinned";
import { API_ENDPOINTS } from "../config/api";
import { getAuthToken } from "../utils/auth";

const APPS_LIST = [
    { id: "pomodoro", name: "Pomodoro", desc: "Focus timer for deep work", defaultMsg: "Pomodoro timer is currently undergoing scheduled updates.", icon: "fi fi-rr-clock", path: "/app-store/pomodoro", color: "#FF6A88" },
    { id: "typewriting", name: "Typewriting", desc: "Speed lessons and typing practice", defaultMsg: "Typewriting practice is temporarily offline for upgrades.", icon: "fi fi-rr-keyboard", path: "/app-store/typewriting", color: "#00b894" },
    { id: "market", name: "Market", desc: "Real-time commodities & indices", defaultMsg: "Market trends service is currently offline for system maintenance.", icon: "fi fi-rr-chart-line-up", path: "/app-store/market", color: "#10b981" },
    { id: "bookstore", name: "Book Store", desc: "Digital books and resources", defaultMsg: "Book Store is temporarily unavailable. Please check back later.", icon: "fi fi-rr-book", path: "/app-store/bookstore", color: "#d35400" },
    { id: "habit-tracker", name: "Habit Tracker", desc: "Streak tracker & daily progress", defaultMsg: "Habit Tracker is temporarily undergoing maintenance.", icon: "fi fi-rr-sparkles", path: "/app-store/habit-tracker", color: "#7C75F8" },
    { id: "brand-my-laptop", name: "Brand My Laptop", desc: "MacBook Pro sticker sponsorship slots", defaultMsg: "Brand My Laptop slots are temporarily unavailable.", icon: "fi fi-rr-laptop", path: "/app-store/brand-my-laptop", color: "#2563eb" },
];

export default function AdminAppStoreSettings() {
    const [navPinned, setNavPinned] = useNavPinned();
    const [isPublic, setIsPublic] = useState(true);
    const [disabledApps, setDisabledApps] = useState({});
    const [appMessages, setAppMessages] = useState({});
    const [maintenanceMessage, setMaintenanceMessage] = useState(
        "App Store is temporarily unavailable for maintenance. Please check back shortly."
    );
    const [updatedAt, setUpdatedAt] = useState(null);
    const [updatedBy, setUpdatedBy] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [togglingAppId, setTogglingAppId] = useState(null);
    const [savingMsgAppId, setSavingMsgAppId] = useState(null);
    const [statusFeedback, setStatusFeedback] = useState({ type: "", text: "" });

    // Fetch current status
    const fetchStatus = async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            const res = await fetch(API_ENDPOINTS.ADMIN.APP_STORE_STATUS, {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
            });

            if (res.ok) {
                const data = await res.json();
                setIsPublic(data.isPublic !== false);
                setDisabledApps(data.disabledApps || {});
                setAppMessages(data.appMessages || {});
                if (data.maintenanceMessage) {
                    setMaintenanceMessage(data.maintenanceMessage);
                }
                setUpdatedAt(data.updatedAt);
                setUpdatedBy(data.updatedBy || "");
            }
        } catch (err) {
            console.error("Failed to fetch App Store status:", err);
            setStatusFeedback({
                type: "error",
                text: "Failed to connect to backend server. Using default public state."
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    // Toggle Master Kill Switch
    const handleToggleMasterSwitch = async () => {
        const nextState = !isPublic;
        setSaving(true);
        setStatusFeedback({ type: "", text: "" });

        try {
            const token = getAuthToken();
            const res = await fetch(API_ENDPOINTS.ADMIN.APP_STORE_STATUS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    isPublic: nextState,
                    disabledApps,
                    appMessages,
                    maintenanceMessage
                })
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setIsPublic(data.isPublic);
                setUpdatedAt(data.updatedAt);
                setUpdatedBy(data.updatedBy);
                setStatusFeedback({
                    type: "success",
                    text: data.isPublic
                        ? "Master switch restored: App Store is now ONLINE."
                        : "Master kill switch engaged: entire App Store is now OFFLINE."
                });
            } else {
                setStatusFeedback({
                    type: "error",
                    text: data.error || "Failed to update App Store status."
                });
            }
        } catch (err) {
            console.error("Error toggling master switch:", err);
            setStatusFeedback({
                type: "error",
                text: "Network error occurred while toggling switch."
            });
        } finally {
            setSaving(false);
        }
    };

    // Toggle Individual App Switch
    const handleToggleAppSwitch = async (appId) => {
        setTogglingAppId(appId);
        setStatusFeedback({ type: "", text: "" });

        const isCurrentlyDisabled = Boolean(disabledApps[appId]);
        const updatedDisabledApps = {
            ...disabledApps,
            [appId]: !isCurrentlyDisabled
        };

        try {
            const token = getAuthToken();
            const res = await fetch(API_ENDPOINTS.ADMIN.APP_STORE_STATUS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    isPublic,
                    disabledApps: updatedDisabledApps,
                    appMessages,
                    maintenanceMessage
                })
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setDisabledApps(data.disabledApps || {});
                setUpdatedAt(data.updatedAt);
                setUpdatedBy(data.updatedBy);
                const appObj = APPS_LIST.find(a => a.id === appId);
                const appName = appObj ? appObj.name : appId;
                setStatusFeedback({
                    type: "success",
                    text: updatedDisabledApps[appId]
                        ? `${appName} was turned OFFLINE.`
                        : `${appName} was turned ONLINE.`
                });
            } else {
                setStatusFeedback({
                    type: "error",
                    text: data.error || "Failed to update app status."
                });
            }
        } catch (err) {
            console.error("Error toggling individual app:", err);
            setStatusFeedback({
                type: "error",
                text: "Network error while updating app status."
            });
        } finally {
            setTogglingAppId(null);
        }
    };

    // Save Message for a specific App
    const handleSaveAppMessage = async (appId) => {
        setSavingMsgAppId(appId);
        setStatusFeedback({ type: "", text: "" });
        const appObj = APPS_LIST.find(a => a.id === appId);
        const appName = appObj ? appObj.name : appId;

        try {
            const token = getAuthToken();
            const res = await fetch(API_ENDPOINTS.ADMIN.APP_STORE_STATUS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    isPublic,
                    disabledApps,
                    appMessages,
                    maintenanceMessage
                })
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setStatusFeedback({
                    type: "success",
                    text: `Maintenance message for ${appName} saved.`
                });
                setUpdatedAt(data.updatedAt);
            } else {
                setStatusFeedback({
                    type: "error",
                    text: data.error || `Failed to save message for ${appName}.`
                });
            }
        } catch (err) {
            setStatusFeedback({
                type: "error",
                text: "Network error while saving app message."
            });
        } finally {
            setSavingMsgAppId(null);
        }
    };

    // Save Master store offline message
    const handleSaveMasterMessage = async () => {
        setSaving(true);
        setStatusFeedback({ type: "", text: "" });
        try {
            const token = getAuthToken();
            const res = await fetch(API_ENDPOINTS.ADMIN.APP_STORE_STATUS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    isPublic,
                    disabledApps,
                    appMessages,
                    maintenanceMessage
                })
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setStatusFeedback({
                    type: "success",
                    text: "Global store maintenance message saved."
                });
                setUpdatedAt(data.updatedAt);
            } else {
                setStatusFeedback({
                    type: "error",
                    text: data.error || "Failed to save message."
                });
            }
        } catch (err) {
            setStatusFeedback({
                type: "error",
                text: "Network error while saving message."
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-container w-full max-w-none px-0 pb-[20px]">
            <AdminTopNav activeTab="appstore" navPinned={navPinned} onTogglePin={() => setNavPinned(v => !v)} />

            <main className="admin-content px-[16px]">
                <div className="appstore-admin-container rounded-[14px] bg-bg p-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
                    {/* Header */}
                    <div className="mb-[24px] flex flex-wrap items-center justify-between gap-[12px] border-b border-slider pb-[16px]">
                        <div>
                            <div className="flex items-center gap-[10px]">
                                <span className="flex h-[36px] w-[36px] items-center justify-center rounded-[10px] bg-primary-cyan/15 text-primary-cyan">
                                    <i className="fi fi-sr-apps" style={{ fontSize: 18 }}></i>
                                </span>
                                <h1 className="text-[1.5rem] font-[800] text-text">App Store Kill Switches</h1>
                            </div>
                            <p className="mt-[4px] text-[0.92rem] text-[#6b7280]">
                                Control public availability globally or toggle individual web applications on and off with custom offline messages.
                            </p>
                        </div>

                        <Link
                            to="/app-store"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-[6px] rounded-[8px] border border-slider px-[12px] py-[7px] text-[0.88rem] font-[600] text-text hover:bg-hover hover:text-primary-cyan"
                        >
                            <span>Open Public App Store</span>
                            <i className="fi fi-rr-arrow-up-right text-[11px]"></i>
                        </Link>
                    </div>

                    {/* Feedback Alert */}
                    {statusFeedback.text && (
                        <div
                            className={`mb-[20px] flex items-center justify-between rounded-[10px] p-[12px] text-[0.92rem] font-[600] ${
                                statusFeedback.type === "error"
                                    ? "border border-red-200 bg-red-50 text-red-700"
                                    : "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            }`}
                        >
                            <div className="flex items-center gap-[10px]">
                                <i className={`fi ${statusFeedback.type === "error" ? "fi-sr-triangle-warning" : "fi-sr-check-circle"}`}></i>
                                <span>{statusFeedback.text}</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setStatusFeedback({ type: "", text: "" })}
                                className="text-current opacity-70 hover:opacity-100"
                            >
                                <i className="fi fi-rr-cross-small"></i>
                            </button>
                        </div>
                    )}

                    {/* Master Global Switch Card */}
                    <div className={`master-killswitch-card mb-[24px] ${isPublic ? "is-online" : "is-killed"}`}>
                        <div className="flex flex-wrap items-center justify-between gap-[16px]">
                            <div className="flex-1 min-w-[260px]">
                                <div className="flex items-center gap-[10px]">
                                    <span className={`status-pill ${isPublic ? "online" : "offline"}`}>
                                        <span className="status-dot"></span>
                                        {isPublic ? "Global Online" : "Master Offline"}
                                    </span>
                                    {updatedAt && (
                                        <span className="text-[0.8rem] text-[#888]">
                                            Updated {new Date(updatedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                            {updatedBy ? ` by ${updatedBy}` : ""}
                                        </span>
                                    )}
                                </div>
                                <h2 className="mt-[6px] text-[1.2rem] font-[800] text-text">
                                    {isPublic ? "Entire App Store is Public" : "Master Kill Switch is Active"}
                                </h2>
                                <p className="mt-[2px] text-[0.88rem] text-[#6b7280]">
                                    {isPublic
                                        ? "Flip to take all apps offline simultaneously under global maintenance mode."
                                        : "All apps are currently disabled for public visitors regardless of individual settings."}
                                </p>
                            </div>

                            <label className="switch-control">
                                <input
                                    type="checkbox"
                                    checked={isPublic}
                                    disabled={loading || saving}
                                    onChange={handleToggleMasterSwitch}
                                />
                                <span className="switch-slider"></span>
                                <span className="text-[0.95rem] font-[700] text-text">
                                    {isPublic ? "Store Online" : "Store Offline"}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Individual Apps Grid */}
                    <div className="mb-[24px]">
                        <div className="mb-[14px] flex items-center justify-between">
                            <div>
                                <h3 className="text-[1.1rem] font-[700] text-text">Individual Application Controls</h3>
                                <p className="text-[0.85rem] text-[#6b7280]">
                                    Toggle each app on or off and tailor custom offline messages for visitors.
                                </p>
                            </div>
                            <span className="text-[0.85rem] font-[600] text-[#6b7280]">
                                {APPS_LIST.filter(a => isPublic && !disabledApps[a.id]).length} / {APPS_LIST.length} Online
                            </span>
                        </div>

                        <div className="grid grid-cols-1 gap-[16px] md:grid-cols-2">
                            {APPS_LIST.map((app) => {
                                const isIndividualDisabled = Boolean(disabledApps[app.id]);
                                const effectiveOnline = isPublic && !isIndividualDisabled;
                                const isAppToggling = togglingAppId === app.id;
                                const isSavingMsg = savingMsgAppId === app.id;
                                const currentMsg = appMessages[app.id] !== undefined ? appMessages[app.id] : app.defaultMsg;

                                return (
                                    <div
                                        key={app.id}
                                        className={`app-switch-card ${isIndividualDisabled ? "is-app-offline" : ""} ${!isPublic ? "is-master-disabled" : ""}`}
                                    >
                                        {/* Top Card Row */}
                                        <div className="app-switch-card-header">
                                            <div className="flex items-center gap-[12px] min-w-0 flex-1">
                                                <span
                                                    className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[10px] text-white shadow-sm"
                                                    style={{ backgroundColor: app.color }}
                                                >
                                                    <i className={app.icon} style={{ fontSize: 18 }}></i>
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-[8px]">
                                                        <span className="truncate text-[0.98rem] font-[700] text-text">
                                                            {app.name}
                                                        </span>
                                                        <span className={`status-pill ${effectiveOnline ? "online" : "offline"}`}>
                                                            <span className="status-dot"></span>
                                                            {effectiveOnline ? "Online" : (isPublic ? "Offline" : "Store Offline")}
                                                        </span>
                                                    </div>
                                                    <span className="block truncate text-[0.8rem] text-[#888]">
                                                        {app.desc}
                                                    </span>
                                                </div>
                                            </div>

                                            <label className="switch-control switch-compact" title={isIndividualDisabled ? `Turn ${app.name} Online` : `Turn ${app.name} Offline`}>
                                                <input
                                                    type="checkbox"
                                                    checked={!isIndividualDisabled}
                                                    disabled={isAppToggling || saving}
                                                    onChange={() => handleToggleAppSwitch(app.id)}
                                                />
                                                <span className="switch-slider"></span>
                                            </label>
                                        </div>

                                        {/* Custom message box for this individual app */}
                                        <div className="app-message-box">
                                            <div className="flex items-center justify-between text-[0.78rem] font-[600] text-[#6b7280]">
                                                <span>Offline message for {app.name}:</span>
                                                {isIndividualDisabled && (
                                                    <span className="text-red-500 font-[700]">Shown to users right now</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-[8px]">
                                                <input
                                                    type="text"
                                                    className="app-message-input"
                                                    value={currentMsg}
                                                    placeholder={`Enter custom offline message for ${app.name}...`}
                                                    onChange={(e) => {
                                                        const newVal = e.target.value;
                                                        setAppMessages(prev => ({
                                                            ...prev,
                                                            [app.id]: newVal
                                                        }));
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveAppMessage(app.id)}
                                                    disabled={isSavingMsg || saving}
                                                    className="shrink-0 rounded-[6px] bg-primary-cyan px-[12px] py-[7px] text-[0.8rem] font-[700] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                                >
                                                    {isSavingMsg ? "Saving..." : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Master Offline Message Config */}
                    <div className="rounded-[14px] border border-slider bg-bg p-[18px]">
                        <div className="mb-[8px] flex items-center justify-between">
                            <div>
                                <h3 className="text-[1rem] font-[700] text-text">Global Store Maintenance Message</h3>
                                <p className="text-[0.82rem] text-[#6b7280]">
                                    Shown when the Master Kill Switch is engaged for all apps at once.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-[10px]">
                            <textarea
                                rows={2}
                                value={maintenanceMessage}
                                onChange={(e) => setMaintenanceMessage(e.target.value)}
                                className="w-full rounded-[10px] border border-slider bg-bg p-[12px] text-[0.9rem] text-text outline-none focus:border-primary-cyan"
                                placeholder="Enter message displayed to visitors when the entire store is offline..."
                            />
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleSaveMasterMessage}
                                    disabled={saving}
                                    className="rounded-[8px] bg-primary-cyan px-[16px] py-[7px] text-[0.88rem] font-[700] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                >
                                    {saving ? "Saving..." : "Save Global Message"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
